import gql from "graphql-tag";
import PatientSummary from "../model/PatientSummary";
import moment from 'moment';
import JourneyCalculator from "../model/JourneyCalculator";
import {UCUMUnit} from "../helper/UCUMUnit";
import {getFilenameFromContentDisposition} from "../helper/headers";

/**
 * Class PatientManager.js
 *
 * @class PatientManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/manager/PatientManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class PatientManager {
    constructor(
        apolloClient,
        apolloMobileClient,
        questionnaireManager,
        store,
        authenticator = null
    ) {
        /** @type ApolloClient */
        this.apolloClient = apolloClient;

        /** @type ApolloClient */
        this.apolloMobileClient = apolloMobileClient;

        /** @type QuestionnaireManager */
        this.questionnaireManager = questionnaireManager;

        /** @type Vuex */
        this.store = store;

        /**
         * @type AuthenticationManager
         */
        this.authenticator = authenticator;

        this._getPatientSummaryPromise = null;
    }

    async getPatientSummary(patientId) {
        if (!patientId || patientId == "0") {
            return Promise.resolve(null)
        }

        patientId = parseInt(patientId);

        if (this._getPatientSummaryPromise !== null) {
            return this._getPatientSummaryPromise
        }

        return this._getPatientSummaryPromise = this.apolloClient
            .query({
                query: gql`query getPatientSummary($patientId : Int!)
        {
            patientSummary(patientId : $patientId){
                outcomeComponents{
                    key
                    active
                }

                outcomeGoals{
                    key
                    value
                    label
                    color
                    extraInformation
                }

                user{
                  id
                  subjectId
                  enrollmentDate
                  knowledgeLevel{
                      key
                      name
                      level
                  }

                    patient{
                        targetWeightValue
                        targetWeightUnit
                        patientId
                        birthdate
                        name{
                            use
                            given
                            family
                        }
                        gender
                    }
                }

                behaviourComponents {
                    status
                    levelOfGuidance
                    key
                    stageKey
                }

                behaviourGoals {
                    key
                    value
                    label
                    color
                    extraInformation
                }


                bloodPressure{
                    component{
                        code{
                            coding{
                                code
                            }
                        }
                        valueQuantity(conversion : [{toUnit : "${ UCUMUnit.MILLIMETER_OF_MERCURY.unit}"}]){
                            id
                            value
                            unit
                        }
                    }
                    status
                }

                weight{
                    valueQuantity(conversion : [{toUnit : "${ UCUMUnit.KILOGRAM.unit}"}]){
                        value
                    }
                }

                LDL{
                    valueQuantity(conversion : [{toUnit : "${ UCUMUnit.MILLIGRAM_PER_DECILITER.unit}"}]){
                        value
                    }
                }
                
                LDLInPreferredUnit {
                    valueQuantity {
                      value
                      unit
                    }
                }

                HbA1c{
                    valueQuantity(conversion : [{toUnit : "${ UCUMUnit.PERCENT.unit}"}]){
                        value
                    }
                }
                
                HbA1cInPreferredUnit {
                    valueQuantity {
                      value
                      unit
                    }
                }

                SDMEncounters{
                    id
                    encounterDate
                    status
                    version
                    DSSRelease
                    DSSVersion
                }

                patientSettings{
                    path
                    value
                }
            }
        }
      `,
                variables: {patientId},
            }).then(result => {
                this._getPatientSummaryPromise = null
                return new PatientSummary(result.data.patientSummary)
            })
    }

    async updateSDMEncounter(version, status, patientId) {
        return this.apolloClient
            .mutate({
                mutation: gql`
          mutation createUpdateSDMEncounter(
              $patientId: Int!
              $version: Int!
              $status: String!
          ) {
              createUpdateSDMEncounter(
                patientId: $patientId
                version: $version
                status: $status
              ) {
                  id
              }
          }
      `,
                variables: {patientId, version, status},
            })
    }

    makeJourneyCalculator(patientId, enrollmentDate) {
        return new JourneyCalculator(
            this.apolloMobileClient,
            this.questionnaireManager,
            patientId,
            enrollmentDate
        )
    }

    async printPrescription(type = 'PATIENT', patientId) {
        let key;

        switch (type.toLowerCase()) {
            case 'patient':
                key = "MEDICATION_SCHEDULES_PATIENT"
                break;
            case 'gp':
                key = "MEDICATION_SCHEDULES_GP"
                break;
            default:
                key = type
        }

        try {
            const { data, errors } = await this.apolloMobileClient.mutate({
                mutation: gql`
                    mutation print(
                        $patientId: Int!,
                        $mediaKey: String!
                    ) {
                        myMedia(mediaKey : $mediaKey, patientId : $patientId)
                    }
                `,
                variables: {
                    mediaKey: key,
                    patientId: parseInt(patientId)
                }
            })

            if (data && !errors) {
                this.getPDFWithAuthHeader(data.myMedia);
                return true
            } else {
                return false
            }
        } catch (error) {
            return false;
        }
    }

    async getPDFWithAuthHeader(pdfLink) {
        // Refresh token to be secure:
        const tokenResult = await this.authenticator.fetchNewTokens(this.authenticator.getRefreshToken());

        if (tokenResult?.authToken !== null) {
            this.store.dispatch('login', tokenResult);
        }

        // do fetch of file here
        const url = pdfLink + '?inline=1';
        const authHeader = 'Bearer ' + this.store.state.authToken;

        const options = {
            headers: new Headers({
                'Authorization': authHeader,
            }),
        };

        let filename = '';
        fetch(url, options)
            .then( res => {
                if (res.status === 200) {
                    filename = getFilenameFromContentDisposition(res.headers.get("content-disposition"));
                }
                return res.blob();
            } )
            .then( blob => {
                const file = window.URL.createObjectURL(blob);
                // window.location.assign(file);
                let a = document.createElement('a');
                a.href = file;
                a.download = filename;
                document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
                a.click();
                a.remove();  //afterwards we remove the element again
            });
    }

    async loadPatientSummary(patientId = null) {
        if (patientId === null) patientId = this.store.state.patientId

        if (!patientId) throw new Error('Missing patient id to load patient summary')

        this.store.commit('setPatientId', patientId)

        return this.getPatientSummary(patientId).then((res) => {
            this.store.commit('setPatientSummary', res)
            return res
        })
    }

    calculateBarriers(questionnaire, resultOfQuery) {
        const entries = resultOfQuery.data.myQuestionnaireForms[0]?.entries;
        const score = resultOfQuery.data.myQuestionnaireForms[0]?.score || 0;
        const entryDate = resultOfQuery.data.myQuestionnaireForms[0]?.entryDate;

        let IMABScore = 0;
        let IMABDate = null;
        let barriersGreen = [];
        let barriersOrange = [];
        let barriersRed = [];
        let text = "";

        if (entries) {
            switch (questionnaire) {
                case 'IMAB':
                    IMABScore = score;
                    IMABDate = moment(entryDate).format('DD/MM/YYYY');

                    entries.forEach((entry) => {
                        const answer = entry.givenAnswers[0].key;

                        switch (entry.question.key) {
                            case "KNOW_HOW":
                                text = "medicationSDMBarriers.knowHow";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersGreen.push(text);
                                } else {
                                    barriersRed.push(text);
                                }
                                break;
                            case "PHYSICALLY_ABLE":
                                text = "medicationSDMBarriers.physicallyAble";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersGreen.push(text);
                                } else {
                                    barriersRed.push(text);
                                }
                                break;
                            case "REMEMBER":
                                text = "medicationSDMBarriers.remember";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersGreen.push(text);
                                } else {
                                    barriersRed.push(text);
                                }
                                break;
                            case "ACCESS":
                                text = "medicationSDMBarriers.gettingHold";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersGreen.push(text);
                                } else {
                                    barriersRed.push(text);
                                }
                                break;
                            case "CONFIDENCE_MANAGING":
                                text = "medicationSDMBarriers.confidence";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersGreen.push(text);
                                } else {
                                    barriersRed.push(text);
                                }
                                break;
                            case "WORRIED_UNWANTED_EFFECTS":
                                text = "medicationSDMBarriers.unwantedEffects";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersRed.push(text);
                                } else {
                                    barriersGreen.push(text);
                                }
                                break;
                            case "BURDEN":
                                text = "medicationSDMBarriers.burden";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersRed.push(text);
                                } else {
                                    barriersGreen.push(text);
                                }
                                break;
                            case "LIFE_IN_WAY":
                                text = "medicationSDMBarriers.lifeInTheWay";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersRed.push(text);
                                } else {
                                    barriersGreen.push(text);
                                }
                                break;
                            case "CANNOT_COPE_CHANGE_REGIME":
                                text = "medicationSDMBarriers.changes";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersRed.push(text);
                                } else {
                                    barriersGreen.push(text);
                                }
                                break;
                            case "WORRIED_OTHER_PEOPLE":
                                text = "medicationSDMBarriers.socialWorries";

                                if (answer === "NEITHER") {
                                    barriersOrange.push(text);
                                } else if (answer === "AGREE" || answer === "STRONGLY_AGREE") {
                                    barriersRed.push(text);
                                } else {
                                    barriersGreen.push(text);
                                }
                                break;
                        }
                    })
                    break;
                case "PHQ-9":
                    text = "medicationSDMBarriers.depressed";
                    if (score <= 4) {
                        barriersGreen.push(text)
                    } else if (score < 10) {
                        barriersOrange.push(text)
                    } else {
                        barriersRed.push(text)
                    }
                    break;
                case "GAD-7":
                    text = "medicationSDMBarriers.anxious";
                    if (score <= 4) {
                        barriersGreen.push(text)
                    } else if (score < 10) {
                        barriersOrange.push(text)
                    } else {
                        barriersRed.push(text)
                    }
                    break;
            }
        }

        if (questionnaire === "IMAB") {
            return {
                questionnaire,
                IMABScore,
                IMABDate,
                barriersGreen,
                barriersOrange,
                barriersRed
            };
        } else {
            return {
                questionnaire,
                barriersGreen,
                barriersOrange,
                barriersRed
            };
        }
    }
}
